import React from 'react';
import {
  ActivityIndicator,
  TouchableHighlight,
  TouchableHighlightProps,
  View,
} from 'react-native';

import { Theme } from '@providers/Theme/theme';
import {
  Variant as FontVariant,
  TypographyProps as ITypographyProps,
  Typography,
} from '@components/Typography';
import { makeStyles, useTheme } from '@providers/Theme';

export type Variant = 'primary' | 'secondary' | 'outlined' | 'none' | 'occean';

export type ButtonProps = TouchableHighlightProps & {
  testID?: string;
  className?: string;
  fullWidth?: boolean;
  onPress?: Function;
  disabled?: boolean;
  variant?: Variant;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  badge?: React.ReactNode;
  TypographyProps?: Partial<Omit<ITypographyProps, 'children'>>;
};

const variantBGColorMap: Record<Variant, (theme: Theme) => string | undefined> =
  {
    none: () => undefined,
    occean: () => undefined,
    primary: (theme) => theme.palette.primary[600],
    secondary: (theme) => theme.palette.grey[400],
    outlined: (theme) => theme.palette.base[100],
  };

const variantActiveBGColorMap: Record<
  Variant,
  (theme: Theme) => string | undefined
> = {
  none: (theme) => theme.palette.grey[200],
  occean: (theme) => theme.palette.grey[200],
  primary: (theme) => theme.palette.primary[400],
  secondary: (theme) => theme.palette.grey[600],
  outlined: (theme) => theme.palette.grey[300],
};

const variantColorMap: Record<Variant, FontVariant> = {
  none: 'base',
  occean: 'occean',
  primary: 'base',
  secondary: 'primary',
  outlined: 'secondary',
};

const variantLoaderMap: Record<Variant, (theme: Theme) => string | undefined> =
  {
    none: () => undefined,
    occean: (theme) => theme.palette.ocean[700],
    primary: (theme) => theme.palette.base[100],
    secondary: (theme) => theme.palette.base[900],
    outlined: (theme) => theme.palette.grey[700],
  };

const useStyles = makeStyles<ButtonProps>((theme) => ({
  root: (props) => ({
    ...theme.mixins.if(props.fullWidth, {
      width: '100%',
    }),
    borderRadius: 8,
    ...theme.mixins.padding(1, props.children ? 2 : 1),
    backgroundColor: props.variant
      ? variantBGColorMap[props.variant](theme)
      : undefined,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    ...theme.mixins.if(props.variant === 'outlined', {
      borderColor: theme.palette.grey[500],
      borderWidth: 1,
      ...theme.mixins.shadow({
        color: `${theme.palette.grey[200]}`,
        opacity: 0.05,
        radius: 2,
        offsetWidth: 0,
        offsetHeight: 1,
        elevation: 30,
        spreadRadius: '0px 0px 0px 4px',
        radiusRGBColor: '0, 0, 0',
      }),
    }),
  }),
  disabled: {
    opacity: 0.5,
  },
  loading: {
    marginRight: 8,
  },
  icon: (props) => ({
    marginRight: props.children ? 8 : 0,
  }),
}));

export const Button: React.FC<ButtonProps> = (props) => {
  const {
    style,
    children,
    onPress,
    disabled,
    variant = 'none',
    loading,
    startIcon,
    endIcon,
    badge,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fullWidth,
    TypographyProps,
    testID,
    ...touchableHighlightProps
  } = props;
  const theme = useTheme();
  const styles = useStyles(props);

  return (
    <TouchableHighlight
      testID={testID}
      style={[styles.root, style, (disabled || loading) && styles.disabled]}
      onPress={onPress}
      disabled={disabled || loading}
      underlayColor={variantActiveBGColorMap[variant](theme)}
      {...touchableHighlightProps}>
      <>
        {loading && (
          <ActivityIndicator
            testID="loading-indicator"
            style={styles.loading}
            color={variantLoaderMap[variant](theme)}
          />
        )}
        {!loading && startIcon && (
          <View testID="start-icon" style={styles.icon}>
            {startIcon}
          </View>
        )}
        {children && (
          <Typography
            testID="label"
            weight="semiBold"
            size={{ xs: 'md', lg: 'xl' }}
            variant={variantColorMap[variant]}
            {...TypographyProps}>
            {children}
          </Typography>
        )}
        {endIcon && (
          <View testID="end-icon" style={styles.icon}>
            {endIcon}
          </View>
        )}
        {badge && <View testID="badge">{badge}</View>}
      </>
    </TouchableHighlight>
  );
};
